// src/components/UserProfile.js
import React, { useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import { getUploadedItemsFromDynamoDB } from '../dynamoService';

const PinIco  = () => <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>;
const CalIco  = () => <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const EditIco = () => <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;

/* ── Stat tile ── */
const Stat = ({ value, label, serif = false }) => (
    <div style={{
        padding: '16px 20px',
        background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 12,
        flex: '1 1 80px', textAlign: 'center',
    }}>
        <div style={{
            fontFamily: serif ? 'var(--serif)' : 'var(--sans)',
            fontSize: 26, fontWeight: 400, color: 'var(--ink)', lineHeight: 1,
        }}>{value}</div>
        <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 4 }}>{label}</div>
    </div>
);

function UserProfile() {
    const [user, setUser] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const u = await Auth.currentAuthenticatedUser();
                setUser(u);
                const data = await getUploadedItemsFromDynamoDB();
                setItems(data);
            } catch (err) {
                setError(err.message?.includes('not authenticated')
                    ? 'You need to be signed in to view this page.'
                    : err.message || 'Failed to load your items.');
                setUser(null);
            } finally { setLoading(false); }
        })();
    }, []);

    const name = user?.attributes?.email?.split('@')[0] || user?.username || 'User';
    const lostC  = items.filter(i => i.itemType === 'lost').length;
    const foundC = items.filter(i => i.itemType === 'found').length;

    if (loading) return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
            <svg style={{ animation: 'spin 0.9s linear infinite' }} width="28" height="28" fill="none" stroke="var(--ink-4)" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
            <p style={{ fontSize: 14, color: 'var(--ink-4)' }}>Loading your profile…</p>
        </div>
    );

    if (error) return (
        <div style={{ minHeight: '100vh', paddingTop: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{
                maxWidth: 380, width: '100%', textAlign: 'center',
                padding: '48px 36px', background: 'var(--white)',
                border: '1px solid var(--border)', borderRadius: 24,
            }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
                <h2 className="display" style={{ fontSize: 26, marginBottom: 10 }}>Sign in required</h2>
                <p style={{ color: 'var(--ink-3)', fontSize: 14, marginBottom: 24 }}>{error}</p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                    <button onClick={() => Auth.federatedSignIn()} className="btn btn-ink">Sign in</button>
                    <button onClick={() => window.location.href = '/'} className="btn btn-outline">Go home</button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="page-wrap" style={{ paddingTop: 60 }}>

            {/* ── Profile header ── */}
            <div style={{
                background: 'var(--white)', borderBottom: '1px solid var(--border)',
                padding: '52px 0 44px',
            }}>
                <div className="container">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                            {/* Avatar */}
                            <div style={{
                                width: 64, height: 64, borderRadius: 18,
                                background: 'var(--ink)', color: 'var(--cream)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontFamily: 'var(--serif)', fontSize: 28, fontWeight: 400, flexShrink: 0,
                            }}>{name[0]?.toUpperCase()}</div>

                            <div>
                                <div className="label-xs" style={{ marginBottom: 6 }}>My profile</div>
                                <h1 className="display" style={{ fontSize: 'clamp(24px, 3vw, 38px)', marginBottom: 6, textTransform: 'capitalize' }}>
                                    {name}
                                </h1>
                                {user?.attributes?.email && (
                                    <p style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 20 }}>{user.attributes.email}</p>
                                )}
                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                    <Stat value={items.length} label="Total reports" serif />
                                    <Stat value={lostC} label="Lost reports" serif />
                                    <Stat value={foundC} label="Found reports" serif />
                                </div>
                            </div>
                        </div>

                        <button onClick={() => window.location.href = '/upload'} className="btn btn-ink">
                            + New report
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Items grid ── */}
            <div className="container" style={{ padding: '40px 32px 80px' }}>
                <div style={{
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', marginBottom: 24, gap: 12, flexWrap: 'wrap',
                }}>
                    <h2 className="display" style={{ fontSize: 26 }}>Your reports</h2>
                    {items.length > 0 && (
                        <span className="tag tag-default">{items.length} item{items.length !== 1 ? 's' : ''}</span>
                    )}
                </div>

                {items.length === 0 ? (
                    <div style={{
                        textAlign: 'center', padding: '80px 24px',
                        background: 'var(--white)', borderRadius: 24,
                        border: '2px dashed var(--border)',
                    }}>
                        <div style={{ fontSize: 48, marginBottom: 14 }}>📭</div>
                        <h3 className="display" style={{ fontSize: 24, marginBottom: 8 }}>No reports yet</h3>
                        <p style={{ color: 'var(--ink-3)', marginBottom: 28, maxWidth: 300, margin: '0 auto 28px' }}>
                            Start by filing your first lost or found report.
                        </p>
                        <button onClick={() => window.location.href = '/upload'} className="btn btn-ink btn-lg">
                            File first report
                        </button>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 20 }}>
                        {items.map((item, i) => (
                            <div key={item.itemId}
                                style={{
                                    background: 'var(--white)', borderRadius: 18,
                                    border: '1px solid var(--border)', overflow: 'hidden',
                                    transition: 'all 0.22s ease',
                                    animation: `stagger 0.4s ease ${i * 55}ms both`,
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                                }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.transform = 'translateY(-3px)';
                                    e.currentTarget.style.boxShadow = '0 10px 32px rgba(0,0,0,0.09)';
                                    e.currentTarget.style.borderColor = 'rgba(26,23,20,0.18)';
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)';
                                    e.currentTarget.style.borderColor = 'var(--border)';
                                }}
                            >
                                {/* Image */}
                                <div style={{
                                    height: 170, overflow: 'hidden',
                                    background: item.itemType === 'lost' ? '#fde8e4' : 'var(--sage-l)',
                                    position: 'relative',
                                }}>
                                    <img src={item.imageUrl} alt={item.itemName || 'Item'}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        onError={e => { e.target.src = "https://placehold.co/400x170/f2efe8/bab5ae?text=No+Image"; }}
                                    />
                                    <span className={`tag ${item.itemType === 'lost' ? 'tag-lost' : 'tag-found'}`}
                                        style={{ position: 'absolute', top: 10, left: 12 }}>
                                        {item.itemType === 'lost' ? '● Lost' : '● Found'}
                                    </span>
                                </div>

                                {/* Body */}
                                <div style={{ padding: '15px 17px' }}>
                                    <h3 style={{
                                        fontFamily: 'var(--serif)', fontSize: 17, fontWeight: 400,
                                        marginBottom: 8, lineHeight: 1.2,
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                    }}>{item.itemName || 'Unnamed Item'}</h3>

                                    {item.description && (
                                        <p style={{
                                            fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5, marginBottom: 10,
                                            display: '-webkit-box', WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                        }}>{item.description}</p>
                                    )}

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
                                        <div style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 12, color: 'var(--ink-4)' }}>
                                            <PinIco />{item.location || 'Unknown'}
                                        </div>
                                        <div style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 12, color: 'var(--ink-4)' }}>
                                            <CalIco />{item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => alert(`Edit "${item.itemName}" — coming soon`)}
                                        className="btn btn-outline btn-sm"
                                        style={{ width: '100%', borderRadius: 8 }}
                                    >
                                        <EditIco /> Edit details
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
export default UserProfile;